/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import {
    HeadingColor,
    HeadingSize,
} from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { MediaQuery } from '@toucan/client/global/application';
import { WrappedLink } from '@toucan/client/global/nextjs';

import { LinkVariant } from '@toucan/client/shared/components/link';

import { ResponsiveImage } from '@toucan/client/blocks/components/responsive-image';

import { styles } from './category-tile.styles';

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type CategoryTileProps = {
    title: string;
    content: string;
    imageSrc: string;
    imageAlt: string;
    buttonText: string;
    buttonUrl: string;
    isLoading?: boolean;
    titleColor?: HeadingColor;
};

const StyledCategoryTile = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

const sizes = {
    [MediaQuery.SMALL_MOBILE]: '29.2rem',
    [MediaQuery.MOBILE]: '38.6rem',
    [MediaQuery.SMALL_TABLET]: '44.8rem',
    [MediaQuery.LARGE_TABLET]: '32.4rem',
    [MediaQuery.DESKTOP]: '42rem',
    [MediaQuery.LARGE_DESKTOP]: '30.2rem',
};

export const CategoryTile: FunctionComponent<CategoryTileProps> = ({
    title,
    content,
    buttonText,
    buttonUrl,
    isLoading,
    imageSrc,
    imageAlt,
    titleColor = HeadingColor.TEXT_SECONDARY,
}: CategoryTileProps) => {
    if (isLoading) {
        return <CategoryTileSkeleton />;
    }

    return (
        <StyledCategoryTile>
            <div className="container">
                <ResponsiveImage
                    src={imageSrc}
                    alt={imageAlt}
                    sizes={sizes}
                    fill
                />
                <div className="overlay-container">
                    <div className="overlay-content">
                        <Heading
                            color={HeadingColor.TEXT_INVERSE}
                            size={HeadingSize.H4}
                        >
                            {title}
                        </Heading>
                        <p>{content}</p>

                        <WrappedLink
                            href={buttonUrl}
                            as={buttonUrl}
                            variant={LinkVariant.CUSTOM}
                        >
                            <Button variant={ButtonVariant.TERTIARY}>
                                {buttonText}
                            </Button>
                        </WrappedLink>
                    </div>
                </div>
            </div>
            <Heading
                size={HeadingSize.H4}
                className="preview-title"
                color={titleColor}
            >
                {title}
            </Heading>
        </StyledCategoryTile>
    );
};

export function CategoryTileSkeleton({ ...props }) {
    return (
        <StyledCategoryTile {...props}>
            <Skeleton
                className="skeleton-card"
                variant={SkeletonVariant.BLOCK}
                lineCount={1}
            />
            <Skeleton
                className="skeleton-title"
                variant={SkeletonVariant.TEXT}
                lineCount={1}
            />
        </StyledCategoryTile>
    );
}
